import { useState } from "react";
import { Building2, KeyRound, Pencil, Plus, Search, Trash2, Wrench } from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Badge, Button, Dialog, Input, Select } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { SkillsDialog } from "@/features/ai/SkillsDialog";
import { UserFormDialog } from "./UserFormDialog";
import { SetPasswordDialog } from "./SetPasswordDialog";
import { DepartmentsDialog } from "./DepartmentsDialog";
import { useDeleteUser, useDepartments, useRoles, useUpdateUser, useUsers } from "./hooks";
import type { ManagedUser } from "./api";

const PAGE_SIZE = 10;

export default function UsersPage() {
  const { user: me } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleCode, setRoleCode] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [tab, setTab] = useState<"internal" | "customer">("internal");
  const isCustomers = tab === "customer";

  const filters = {
    page,
    page_size: PAGE_SIZE,
    search: search.trim(),
    // The role filter only applies to internal staff; customers are all one role.
    role_code: isCustomers ? "" : roleCode,
    account_type: tab,
    ...(activeFilter === "" ? {} : { is_active: activeFilter === "active" }),
  };

  function switchTab(next: "internal" | "customer") {
    setTab(next);
    setPage(1);
    setRoleCode("");
    setActiveFilter("");
    setSearch("");
  }

  const { data, isLoading, isError, error } = useUsers(filters);
  const { data: roles } = useRoles();
  const { data: departments } = useDepartments();
  const update = useUpdateUser();
  const remove = useDeleteUser();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ManagedUser | null>(null);
  const [skillsFor, setSkillsFor] = useState<ManagedUser | null>(null);
  const [pwFor, setPwFor] = useState<ManagedUser | null>(null);
  const [deleting, setDeleting] = useState<ManagedUser | null>(null);
  const [deptsOpen, setDeptsOpen] = useState(false);
  const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  async function run(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      // Server-side rules (last admin, self-deactivation) surface here.
      setActionError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isCustomers
              ? "Customer portal accounts. Activate a pending signup to approve it."
              : "Internal staff accounts, roles and departments."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RequirePermission permission="settings:manage">
            <Button variant="outline" onClick={() => setDeptsOpen(true)}>
              <Building2 className="h-4 w-4" aria-hidden="true" />
              Departments
            </Button>
          </RequirePermission>
          <RequirePermission permission="user:create">
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add user
            </Button>
          </RequirePermission>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border" role="tablist" aria-label="Account type">
        {([
          ["internal", "Internal users"],
          ["customer", "Customers"],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => switchTab(value)}
            className={
              "-mb-px border-b-2 px-4 py-2 text-sm font-medium " +
              (tab === value
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {actionError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {actionError}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            aria-label="Search users"
            placeholder="Search name, email or designation"
            className="pl-8"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1); // a filtered result set makes the old page number meaningless
            }}
          />
        </div>
        {!isCustomers && (
          <Select
            aria-label="Filter by role"
            className="w-auto min-w-[150px]"
            value={roleCode}
            onChange={(e) => {
              setRoleCode(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All roles</option>
            {(roles ?? [])
              .filter((r) => r.code !== "customer")
              .map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
          </Select>
        )}
        <Select
          aria-label="Filter by status"
          className="w-auto min-w-[130px]"
          value={activeFilter}
          onChange={(e) => {
            setActiveFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Role</th>
              <th className="px-4 py-2.5 font-medium">Department</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Last login</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load users."}
                </td>
              </tr>
            )}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  No users match these filters.
                </td>
              </tr>
            )}
            {data?.items.map((u) => (
              <tr key={u.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="font-medium">{u.full_name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  {u.role_name}
                  {u.sites && u.sites.length > 0 && (
                    <div
                      className="text-xs text-muted-foreground"
                      title={u.sites.map((l) => l.name).join(", ")}
                    >
                      {u.sites.length} site{u.sites.length === 1 ? "" : "s"}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {u.department_name ?? "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tone={u.is_active ? "success" : "danger"}>
                      {u.is_active ? "Active" : "Inactive"}
                    </Badge>
                    {u.must_change_password && <Badge>Password reset pending</Badge>}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {u.last_login_at ? formatDateTime(u.last_login_at) : "Never"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <RequirePermission permission="user:update">
                      <Button
                        variant="ghost"
                        aria-label={`Edit ${u.full_name}`}
                        onClick={() => {
                          setEditing(u);
                          setFormOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {u.role_code.includes("engineer") && (
                        <Button
                          variant="ghost"
                          aria-label={`Skills for ${u.full_name}`}
                          onClick={() => setSkillsFor(u)}
                        >
                          <Wrench className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        aria-label={`Reset password for ${u.full_name}`}
                        onClick={() => setPwFor(u)}
                      >
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        // Self-deactivation is refused server-side; disabling
                        // it here avoids offering an action that cannot work.
                        disabled={u.id === me?.id}
                        onClick={() =>
                          void run(() =>
                            update.mutateAsync({
                              id: u.id,
                              body: { is_active: !u.is_active },
                            }),
                          )
                        }
                      >
                        {u.is_active ? "Deactivate" : "Activate"}
                      </Button>
                    </RequirePermission>
                    <RequirePermission permission="user:delete">
                      <Button
                        variant="ghost"
                        aria-label={`Remove ${u.full_name}`}
                        disabled={u.id === me?.id}
                        onClick={() => setDeleting(u)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </RequirePermission>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {data ? `${data.total} user${data.total === 1 ? "" : "s"}` : ""}
          {departments ? ` · ${departments.length} departments` : ""}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <SkillsDialog
        userId={skillsFor?.id ?? null}
        userName={skillsFor?.full_name ?? ""}
        onClose={() => setSkillsFor(null)}
      />

      {pwFor && (
        <SetPasswordDialog
          user={pwFor}
          onClose={() => setPwFor(null)}
          onGenerated={(temporaryPassword) => {
            const email = pwFor.email;
            setPwFor(null);
            setNotice({
              title: "Temporary password issued",
              body: `${email}\n\n${temporaryPassword}\n\nAll existing sessions were signed out. This is shown once.`,
            });
          }}
        />
      )}

      <UserFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        editing={editing}
        onCreated={(pw, email) =>
          setNotice({
            title: "User created",
            body: `${email}\n\n${pw}\n\nThey must change this password at first sign-in. This is shown once.`,
          })
        }
      />

      <DepartmentsDialog open={deptsOpen} onClose={() => setDeptsOpen(false)} />

      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Remove user?"
      >
        <p className="text-sm text-muted-foreground">
          This deactivates and removes{" "}
          <span className="font-medium text-foreground">{deleting?.full_name}</span>{" "}
          ({deleting?.email}) and signs them out of all sessions. You can re-create
          the account later.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => {
              const target = deleting;
              if (!target) return;
              // Close first so a server refusal (e.g. last admin) shows in the
              // page-level alert rather than behind this modal.
              setDeleting(null);
              void run(() => remove.mutateAsync(target.id));
            }}
          >
            Remove user
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={notice !== null}
        onClose={() => setNotice(null)}
        title={notice?.title ?? ""}
      >
        <pre className="whitespace-pre-wrap break-all rounded-md bg-muted p-3 text-sm">
          {notice?.body}
        </pre>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => setNotice(null)}>Done</Button>
        </div>
      </Dialog>
    </div>
  );
}
